<template>
  <div ref="contentPreviewModal" class="uk-modal uk-modal-full" uk-modal>
    <div class="uk-modal-dialog content-preview-modal-fullscreen">
      <!-- Close Button -->
      <button class="uk-modal-close-full uk-close-large" type="button" uk-close>
        <i class="uil-times"></i>
      </button>

      <div class="modal-header">
        <div class="modal-title-section">
          <p class="modal-subtitle">{{ localSelectedContent?.title }}</p>
        </div>
      </div>

      <div class="uk-modal-body">
        <default-loading v-if="previewLoading" />

        <!-- Content previews -->
        <div v-else-if="localPreviewContent" class="preview-content">

          <!-- SCORM Content (file_category_id: 1) -->
          <div v-if="localPreviewContent.file_category_id === 1" class="iframe-responsive">
            <iframe
                :src="localPreviewContent.playUrl"
                :title="localSelectedContent?.title || $t('general.preview')"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; camera; microphone"
                allowfullscreen
                frameborder="0">
            </iframe>
          </div>

          <!-- Image Content (file_category_id: 2) -->
          <div v-if="localPreviewContent.file_category_id === 2" class="image-preview">
            <img :src="localPreviewContent.fileUrl" :alt="$t('general.preview')"
                 style="max-width: 100%; max-height: 400px; height: auto; object-fit: contain; border-radius: 8px;"/>
          </div>

          <!-- PDF Content (file_category_id: 3) -->
          <div v-if="localPreviewContent.file_category_id === 3" class="iframe-responsive">
            <iframe
                :src="localPreviewContent.fileUrl + '#toolbar=0&navpanes=0&scrollbar=0'"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; camera; microphone"
                :title="$t('general.preview')">
            </iframe>
          </div>

          <!-- Document Content (Word, PowerPoint - file_category_id: 4, 5) -->
          <div v-if="[4, 5].includes(localPreviewContent.file_category_id)" class="document-preview">
            <!-- Office Online Viewer for preview -->
            <div class="iframe-responsive mt-3">
              <iframe
                  :src="'https://view.officeapps.live.com/op/embed.aspx?src=' + encodeURIComponent(localPreviewContent.fileUrl) + '?postMessageEvents=true'"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; camera; microphone"
                  allowfullscreen
                  frameborder="0"
                  mozallowfullscreen
                  scrolling="no"
                  webkitallowfullscreen>
              </iframe>
            </div>
          </div>

          <!-- Audio Content (file_category_id: 6) -->
          <div v-if="localPreviewContent.file_category_id === 6" class="audio-preview">
            <audio-player :audioUrl="localPreviewContent.fileUrl" />
          </div>

          <!-- Video Content (file_category_id: 7) -->
          <div v-if="localPreviewContent.file_category_id === 7" class="video-responsive">
            <div ref="artplayerContainer" class="artplayer-app">
              <video-player v-if="localPreviewContent.file_category_id === 7"
                            :videoUrl="localPreviewContent.videoUrl || localPreviewContent.fileUrl" />
            </div>
          </div>

          <!-- HTML/Web Content (file_category_id: 8) -->
          <div v-if="localPreviewContent.file_category_id === 8" class="iframe-responsive">
            <iframe
                :src="localPreviewContent.fileUrl || localPreviewContent.video_url ||  localPreviewContent.url"
                :title="localSelectedContent?.title || $t('general.preview')"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                frameborder="0">
            </iframe>
          </div>

          <!-- External Link Content (file_category_id: 9) -->
          <div v-if="localPreviewContent.file_category_id === 9" class="iframe-responsive">
            <iframe
                :src="localPreviewContent.fileUrl || localPreviewContent.linkUrl ||  localPreviewContent.url"
                :title="localSelectedContent?.title || $t('general.preview')"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; camera; microphone"
                frameborder="0">
            </iframe>
          </div>

          <!-- External SCORM Content (file_category_id: 10) -->
          <div v-if="localPreviewContent.file_category_id === 10" class="iframe-responsive">
            <iframe
                :src=" localPreviewContent.fileUrl || localPreviewContent.externalScormUrl || localPreviewContent.anzeraUrl"
                :title="localSelectedContent?.title || $t('general.preview')"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; camera; microphone"
                frameborder="0">
            </iframe>
          </div>

          <!-- Exam Content (file_category_id: 12) -->
          <div v-if="localPreviewContent.file_category_id === 12" class="exam-preview">
            <ExamQuestionList :exam="localPreviewContent.exam" :modal_name="'assignment_exam'"
                              @onClose="hideModal" @onSubmit="hideModal"></ExamQuestionList>
          </div>

          <!-- Survey Content (file_category_id: 13) -->
          <div v-if="localPreviewContent.file_category_id === 13" class="survey-preview">
            <!--            <QuestionList :modal_name="'assignment_survey'" -->
            <!--                          :survey="[]" @onClose="hideModal" @onSubmit="hideModal"></QuestionList>-->

          </div>

          <!-- Classroom Training (file_category_id: 14) -->
          <div v-if="localPreviewContent.file_category_id === 14" class="classroom-preview">
            {{$t('general.classroom_info')}}
          </div>

          <!-- Checklist Content - kontrol listesi (file_category_id: 15) -->
          <div v-if="localPreviewContent.file_category_id === 15" class="check-list-preview">
            <template>
              <CheckList
                  :checkList="localPreviewContent.checkListItem || []"
                  :modal_name="'checklist_preview'"
                  :modal_title="localSelectedContent?.title"
                  @onClose="hideModal">
              </CheckList>
            </template>
          </div>

          <!-- Mission Content (file_category_id: 16) -->
          <div v-if="localPreviewContent.file_category_id === 16" class="mission-content-preview mission-preview">
            <Mission
              :mission="localPreviewContent.mission || localPreviewContent.missionDetail || localPreviewContent"
              :modal_name="'mission_preview'"
              :modal_title="localSelectedContent?.title"
              :preview="true"
              @onClose="hideModal"
            />
          </div>

          <!-- Information Content (file_category_id: 17) -->
          <div v-if="localPreviewContent.file_category_id === 17" class="information-preview">
            <div class="uk-width-5-5m uk-first-column"
                 v-html="localPreviewContent.informationContent?.information_content"></div>
          </div>

          <!-- Webinar (file_category_id: 18) -->
          <div v-if="localPreviewContent.file_category_id === 18" class="webinar-preview">
            <div class="uk-alert-info" uk-alert>
              <a v-if="localPreviewContent.webinar.join_url"
                 :href="localPreviewContent.webinar.join_url"
                 class="btn btn-primary btn-sm"
                 target="_blank"
                 @click="openWebinarInNewTab">
                <i class="uil-external-link-alt"></i> {{ $t('general.join_webinar') }}
              </a>
            </div>
          </div>

          <!-- Jitsi Meeting (file_category_id: 19) -->
          <div v-if="localPreviewContent.file_category_id === 19" class="jitsi-preview">
            <div class="iframe-responsive">
              <iframe
                  :src="localPreviewContent.jitsi_url || localPreviewContent.url || localPreviewContent.fileUrl"
                  :title="localSelectedContent?.title || $t('general.preview')"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; camera; microphone"
                  allowfullscreen
                  frameborder="0">
              </iframe>
            </div>
          </div>

          <!-- Unknown types -->
          <div
              v-if="!localPreviewContent.file_category_id || ![1,2,3,4,5,6,7,8,9,10,12,13,14,15,16,17,18,19].includes(localPreviewContent.file_category_id)"
              class="uk-alert-warning" uk-alert>
            <h4><i class="uil-exclamation-triangle"></i> {{ $t('general.preview_not_supported') }}</h4>
            <div v-if="localPreviewContent.fileUrl">
              <a :href="localPreviewContent.fileUrl" class="btn btn-secondary btn-sm" target="_blank">
                <i class="uil-external-link-alt"></i> {{ $t('general.open_in_new_tab') }}
              </a>
            </div>
          </div>
        </div>

        <!-- Error -->
        <div v-else-if="previewError" class="uk-alert-danger" uk-alert>
          <p style="color: red">
            <i class="uil-exclamation-triangle"></i> {{ previewError }}
          </p>
        </div>

        <!-- No content state -->
        <div v-else class="uk-alert-warning" uk-alert>
          <p>
            <i class="uil-info-circle"></i> {{ $t('general.no_preview_available') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import CheckList from "@/view/components/checkList/CheckListDetail.vue";
import Mission from "@/view/components/mission/MissionDetail.vue";
import ExamQuestionList from "@/view/components/exam/ExamQuestionList.vue";
import QuestionList from "@/view/components/survey/QuestionList.vue";
import VideoPlayer from "@/view/components/video/VideoPlayer.vue";
import AudioPlayer from "@/view/components/audio/AudioPlayer.vue";

export default {
  name: "ContentPreviewModal",
  components: { QuestionList, ExamQuestionList, DefaultLoading, CheckList, Mission, VideoPlayer, AudioPlayer },
  props: {
    selectedContent: {
      type: Object,
      default: null
    },
    previewContent: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      artPlayer: null,
      previewLoading: false,
      previewError: null,
      localSelectedContent: null,
      localPreviewContent: null,
    }
  },
  methods: {
    showModal() {
      this.stopAllIframes();
      this.previewError = null;
      this.previewLoading = false;
      this.localSelectedContent = this.selectedContent;
      this.localPreviewContent = this.previewContent;

      UIkit.modal(this.$refs.contentPreviewModal).show();
    },
    hideModal() {
      this.clearPreviousData();
      this.stopAllIframes();

      UIkit.modal(this.$refs.contentPreviewModal).hide();
    },
    clearPreviousData() {
      this.stopAllIframes();

      const container = this.$refs.artplayerContainer;
      if (container) {
        container.innerHTML = '';
      }

      this.localPreviewContent = null;
      this.localSelectedContent = null;
      this.previewError = null;
      this.previewLoading = false;
    },

    stopAllIframes() {
      // Modal içindeki tüm iframe'leri durdur (Scorm,video,sound)
      const modal = this.$refs.contentPreviewModal;
      if (modal) {
        const iframes = modal.querySelectorAll('iframe');
        iframes.forEach(iframe => {
          try {
            // Cross-origin iframe'leri güvenli şekilde durdur
            if (iframe.src && iframe.src !== 'about:blank') {
              // Iframe'i güvenli şekilde durdur
              iframe.src = 'about:blank';
              
              // DOM'dan kaldır
              setTimeout(() => {
                if (iframe.parentNode) {
                  iframe.parentNode.removeChild(iframe);
                }
              }, 100);
            }
          } catch (error) {
            console.error('Error stopping iframe:', error);
            // Hata varsa iframe'i kaldır
            try {
              if (iframe.parentNode) {
                iframe.parentNode.removeChild(iframe);
              }
            } catch (e) {
              console.error('Error removing iframe:', e);
            }
          }
        });

        const mediaElements = modal.querySelectorAll('video, audio');
        mediaElements.forEach(media => {
          try {
            media.pause();
            media.currentTime = 0;
            media.src = '';
            media.load();
          } catch (error) {
            console.error('Error stopping media element:', error);
          }
        });
      }
    },
    openWebinarInNewTab() {
      const webinarUrl = this.localPreviewContent.webinar.join_url;
      if (webinarUrl) {
        window.open(webinarUrl, '_blank');
      }
    },
  },
  watch: {
    selectedContent: {
      handler(newContent) {
        this.localSelectedContent = newContent;
      },
      deep: true,
    },
    previewContent: {
      handler(newContent) {
        this.localPreviewContent = newContent;
      },
      deep: true,
    },
  },
  mounted() {
    // Modal kapatma eventi ekleme - X butonu
    const modal = this.$refs.contentPreviewModal;
    if (modal) {
      UIkit.util.on(modal, 'hide', () => {
        this.clearPreviousData();
        this.stopAllIframes();
      });
    }
  },
  beforeDestroy() {
    this.stopAllIframes();
  }
}
</script>
<style>
.check-list-preview {
  .section-header, .card-footer {
    display: none !important;
  }
}
.mission-content-preview {
  .card-footer {
    display: none !important;
  }
}
</style>

<style scoped>
.content-preview-modal-fullscreen {
  width: 100vw;
  height: 100vh;
  max-width: none;
  max-height: none;
  margin: 0;
  border-radius: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.uk-modal-close-full {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1001;
  width: 50px;
  height: 50px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.uk-modal-close-full:hover {
  background: rgba(0, 0, 0, 0.9);
  transform: scale(1.1);
}

.uk-modal-close-full i {
  font-size: 1rem;
}

.modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
  color: #495057;
  flex-shrink: 0;
}

.modal-title-section {
  flex: 1;
}

.modal-header .uk-modal-title {
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.modal-header .uk-modal-title i {
  font-size: 1.125rem;
  color: #6c757d;
}

.modal-subtitle {
  font-size: 0.8rem;
  color: #6c757d;
  margin: 0.25rem 0 0 0;
  font-weight: 400;
  line-height: 1.3;
  max-width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.uk-modal-body {
  padding: 1.5rem;
  background-color: #fff;
  flex: 1;
  overflow-y: auto;
  position: relative;
  height: calc(100vh - 80px);
}

.preview-content {
  min-height: 300px;
}

.video-responsive {
  width: 100%;
  height: calc(100vh - 200px);
  min-height: 600px;
  max-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-preview {
  text-align: center;
  margin-bottom: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.iframe-responsive {
  width: 100%;
  height: calc(100vh - 200px);
  min-height: 600px;
  max-height: calc(100vh - 200px);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.iframe-responsive iframe {
  border-radius: 8px;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.document-preview {
  text-align: center;
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin: 1rem 0;
}

.document-preview .uk-alert-primary {
  border-left: 4px solid #007bff;
}

.artplayer-app {
  width: 100%;
  height: calc(100vh - 200px);
  min-height: 600px;
  max-height: calc(100vh - 200px);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.information-preview {
  padding: 1.5rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin: 1rem 0;
}

.information-preview ::v-deep p {
  line-height: 1.6;
  color: #6c757d;
  margin-bottom: 1rem;
}

.information-preview ::v-deep img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

.classroom-preview .classroom-cover img {
  border-radius: 0.375rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.classroom-preview .preview-notice {
  margin-bottom: 1.5rem;
}

.classroom-preview .notice-box {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 0.375rem;
  padding: 1rem;
  text-align: center;
}

.classroom-preview .notice-box i {
  font-size: 2rem;
  color: #856404;
  margin-bottom: 0.5rem;
  display: block;
}

.classroom-preview .notice-box p {
  margin-bottom: 0.5rem;
  color: #856404;
}

.classroom-preview .info-title {
  color: #495057;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

.classroom-preview .info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.classroom-preview .info-item {
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  padding: 0.75rem;
  border-radius: 0.25rem;
  border-left: 3px solid #007bff;
}

.classroom-preview .info-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.classroom-preview .info-value {
  color: #6c757d;
  font-size: 0.9rem;
}

.classroom-preview .info-subtitle {
  color: #495057;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.classroom-preview .description-box {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;
  color: #495057;
  line-height: 1.5;
}

/* Responsive Design */
@media (max-width: 768px) {
  .uk-modal-close-full {
    top: 15px;
    right: 15px;
    width: 45px;
    height: 45px;
  }

  .uk-modal-close-full i {
    font-size: 1.3rem;
  }

  .modal-header {
    padding: 1rem;
  }

  .uk-modal-body {
    padding: 1rem;
    height: calc(100vh - 100px);
  }

  .iframe-responsive {
    height: calc(100vh - 180px);
    min-height: 400px;
    max-height: calc(100vh - 180px);
  }

  .artplayer-app {
    height: calc(100vh - 180px);
    min-height: 400px;
    max-height: calc(100vh - 180px);
  }

  .video-responsive {
    height: calc(100vh - 180px);
    min-height: 400px;
    max-height: calc(100vh - 180px);
  }
}

@media (max-width: 480px) {
  .uk-modal-close-full {
    top: 10px;
    right: 10px;
    width: 40px;
    height: 40px;
  }

  .uk-modal-close-full i {
    font-size: 1.2rem;
  }

  .modal-header {
    padding: 0.75rem;
  }

  .uk-modal-body {
    padding: 0.75rem;
    height: calc(100vh - 80px);
  }

  .iframe-responsive {
    height: calc(100vh - 150px);
    min-height: 300px;
    max-height: calc(100vh - 150px);
  }

  .artplayer-app {
    height: calc(100vh - 150px);
    min-height: 300px;
    max-height: calc(100vh - 150px);
  }

  .video-responsive {
    height: calc(100vh - 150px);
    min-height: 300px;
    max-height: calc(100vh - 150px);
  }
}
</style>
